import { livePreviewEnabled, reviewerPreviewSandboxEnabled, workflowConfig, photoLibraryEnabled } from "./auth";

export class WorkflowApiError extends Error {
  constructor(message, status, code) {
    super(message);
    this.name = "WorkflowApiError";
    this.status = status;
    this.code = code;
  }
}

async function responseError(response) {
  try {
    const body = await response.json();
    const code = body?.error?.code || "request_failed";
    let detail = body?.error?.message || "The workflow request failed.";
    if (code === "generic_role_not_allowed" && /Only Support, Direct Updater, and Admin roles can be managed here\.?/i.test(detail)) {
      detail = "Reviewer is part of the managed role set, but the connected workflow API has not been upgraded yet. Managed roles are Support, Reviewer, Direct Updater, and Admin.";
    }
    return new WorkflowApiError(detail, response.status, code);
  } catch {
    return new WorkflowApiError("The workflow request failed.", response.status, "request_failed");
  }
}

async function requestAgainst(baseUrl, path, getAccessToken, options = {}) {
  const token = await getAccessToken();
  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
    },
  });
  if (!response.ok) throw await responseError(response);
  if (response.status === 204) return null;
  return response.json();
}

const request = (path, getAccessToken, options = {}) => requestAgainst(workflowConfig.apiUrl, path, getAccessToken, options);

function sandboxWriteBlocked() {
  return Promise.reject(new WorkflowApiError(
    "This action is disabled in the Preview sandbox so production workflow settings and Microsoft 365 are not changed.",
    423,
    "preview_sandbox_write_blocked",
  ));
}

function blobBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The finished photo could not be prepared."));
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.readAsDataURL(blob);
  });
}

export const newIdempotencyKey = () => crypto.randomUUID();

async function originalBase64(file) {
  if (!(file instanceof Blob)) throw new Error('Select the original photo again before submitting.');
  if (file.size > 25 * 1024 * 1024 || !['image/png', 'image/jpeg'].includes(file.type)) throw new Error('The original must be a PNG or JPEG no larger than 25 MB.');
  return blobBase64(file);
}
export const getPhotoLibraryAccess = getAccessToken => request('/photo-library/access', getAccessToken);
export const listPhotoLibrary = (getAccessToken, filters = {}) => request(`/photo-library/employees?${new URLSearchParams(filters)}`, getAccessToken);
export async function fetchLibraryVariant(photo, variant, getAccessToken, preview = false) {
  const token = await getAccessToken();
  const response = await fetch(`${workflowConfig.apiUrl}/photo-library/employees/${encodeURIComponent(photo.employeeId)}/versions/${encodeURIComponent(photo.versionId)}/${variant}?${new URLSearchParams({ preview: String(preview) })}`, {
    headers: { Authorization: `Bearer ${token}` }, cache: 'no-store',
  });
  if (!response.ok) throw await responseError(response);
  return response.blob();
}

export async function checkPhotoQuality(blob, getAccessToken) {
  return request("/quality-check", getAccessToken, {
    method: "POST",
    body: JSON.stringify({ imageBase64: await blobBase64(blob) }),
  });
}

export async function submitOwnPhoto(blob, getAccessToken, idempotencyKey, originalPhoto) {
  if (reviewerPreviewSandboxEnabled) return sandboxWriteBlocked();
  return request("/submissions", getAccessToken, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify({ imageBase64: await blobBase64(blob), ...(photoLibraryEnabled ? { originalImageBase64: await originalBase64(originalPhoto) } : {}) }),
  });
}

export function requestPhotoRemoval(getAccessToken, idempotencyKey) {
  if (reviewerPreviewSandboxEnabled) return sandboxWriteBlocked();
  return request("/submissions/removal", getAccessToken, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
  });
}

export async function submitDirectPhoto(blob, employeeEmail, reasonCode, comments, getAccessToken, idempotencyKey, originalPhoto) {
  if (reviewerPreviewSandboxEnabled) return sandboxWriteBlocked();
  return request("/direct-updates", getAccessToken, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify({ employeeEmail, reasonCode, comments, imageBase64: await blobBase64(blob), ...(photoLibraryEnabled ? { originalImageBase64: await originalBase64(originalPhoto) } : {}) }),
  });
}

export async function getWorkflowIdentity(getAccessToken) {
  const result = await request("/me", getAccessToken);
  if (livePreviewEnabled) {
    const access = await request("/reviews/access", getAccessToken);
    return { ...result, identity: { ...result.identity, capabilities: { ...result.identity?.capabilities, reviewer: access.reviewer === true } } };
  }
  if (!reviewerPreviewSandboxEnabled) return result;
  const raw = result?.identity || {};
  const previewReviewer = raw?.capabilities?.reviewer === true
    || raw?.capabilities?.administration?.manageRoles === true;
  return {
    ...result,
    previewSandbox: true,
    identity: {
      ...raw,
      capabilities: {
        ...(raw.capabilities || {}),
        reviewer: previewReviewer,
      },
    },
  };
}
export const getServiceAvailability = (getAccessToken) => request("/me/service-availability", getAccessToken);
export const getAdminRoleProjection = (getAccessToken) => request('/management/roles', getAccessToken);
export const getAdminCcdApprovers = (getAccessToken) => request('/management/ccd-approvers', getAccessToken);
export const lookupAdminUsers = (query, getAccessToken) => request(`/management/users?query=${encodeURIComponent(query)}`, getAccessToken);
export const getSubmission = (id, getAccessToken) => request(`/submissions/${encodeURIComponent(id)}`, getAccessToken);
export const listMySubmissions = (getAccessToken) => request("/me/submissions", getAccessToken);
export const getSubmissionHistory = (id, getAccessToken) => request(`/submissions/${encodeURIComponent(id)}/history`, getAccessToken);
export const getReviewerHistory = (id, getAccessToken) => request(`/reviews/${encodeURIComponent(id)}/history`, getAccessToken);
export const cancelSubmission = (id, getAccessToken) => reviewerPreviewSandboxEnabled
  ? sandboxWriteBlocked()
  : request(`/submissions/${encodeURIComponent(id)}/cancel`, getAccessToken, { method: "POST" });
export const listApprovals = (getAccessToken) => request("/approvals", getAccessToken);
export const listReviewRequests = (getAccessToken) => request("/reviews", getAccessToken);
export async function getReviewerHcWorkflow(getAccessToken) {
  try {
    return await request("/reviews/hc-workflow", getAccessToken);
  } catch (error) {
    // Preview 45 uses the production API. Until the separately protected API
    // release includes this GET-only endpoint, Azure returns a non-JSON 404.
    if (error instanceof WorkflowApiError && error.status === 404) {
      throw new WorkflowApiError(
        "The connected API does not have the HC approver directory endpoint yet. A separate read-only API release is required to display live HC names.",
        404, "hc_directory_endpoint_missing",
      );
    }
    if (error instanceof WorkflowApiError && error.status === 403) {
      throw new WorkflowApiError(
        "This account is not authorized for the HC approver directory. Reviewer or verified Central CCD approval permission is required.",
        403, "hc_directory_forbidden",
      );
    }
    throw error;
  }
}
export const listOperations = (getAccessToken) => request("/operations", getAccessToken);
export const getAdminSettingsPreview = (getAccessToken) => request("/management/settings", getAccessToken);

export function changeAdminRoleAssignment(email, role, action, reason, getAccessToken) {
  if (reviewerPreviewSandboxEnabled) return sandboxWriteBlocked();
  return request('/management/roles/assignment', getAccessToken, {
    method: 'POST',
    body: JSON.stringify({ email, role, action, reason }),
  });
}

export function reconcileAdminHcRoles(reason, getAccessToken) {
  if (reviewerPreviewSandboxEnabled) return sandboxWriteBlocked();
  return request('/management/roles/reconcile-hc', getAccessToken, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export function saveAdminPolicy(policy, expectedVersion, reason, getAccessToken) {
  if (reviewerPreviewSandboxEnabled) return sandboxWriteBlocked();
  return request("/management/settings/policy", getAccessToken, {
    method: "PUT",
    body: JSON.stringify({ policy, expectedVersion, reason }),
  });
}

export function saveAdminCompanyRoute(route, expectedVersion, reason, getAccessToken) {
  if (reviewerPreviewSandboxEnabled) return sandboxWriteBlocked();
  return request("/management/settings/routes", getAccessToken, {
    method: "PUT",
    body: JSON.stringify({ route, expectedVersion, reason }),
  });
}

export function removeAdminCompanyRoute(companyKey, expectedVersion, reason, getAccessToken) {
  if (reviewerPreviewSandboxEnabled) return sandboxWriteBlocked();
  return request("/management/settings/routes/remove", getAccessToken, {
    method: "POST",
    body: JSON.stringify({ companyKey, expectedVersion, reason }),
  });
}

export function saveAdminFallbackHc(approverEmails, expectedVersion, reason, getAccessToken) {
  if (reviewerPreviewSandboxEnabled) return sandboxWriteBlocked();
  return request("/management/settings/fallback-hc", getAccessToken, {
    method: "PUT",
    body: JSON.stringify({ approverEmails, expectedVersion, reason }),
  });
}

export function saveAdminNotificationRecipients(recipients, expectedVersion, reason, getAccessToken) {
  if (reviewerPreviewSandboxEnabled) return sandboxWriteBlocked();
  return request("/management/settings/notifications", getAccessToken, {
    method: "PUT",
    body: JSON.stringify({ recipients, expectedVersion, reason }),
  });
}

export function saveAdminCcdApprovers(approverEmails, expectedVersion, reason, getAccessToken) {
  if (reviewerPreviewSandboxEnabled) return sandboxWriteBlocked();
  return request('/management/ccd-approvers', getAccessToken, {
    method: 'PUT',
    body: JSON.stringify({ approverEmails, expectedVersion, reason }),
  });
}

export function decideApproval(id, stage, decision, comments, reasonCode, getAccessToken) {
  if (reviewerPreviewSandboxEnabled) return sandboxWriteBlocked();
  return request(`/approvals/${encodeURIComponent(id)}/${stage}`, getAccessToken, {
    method: "POST",
    body: JSON.stringify({ decision, comments, reasonCode }),
  });
}

export async function getSubmissionPhoto(id, getAccessToken) {
  if (reviewerPreviewSandboxEnabled) return sandboxWriteBlocked();
  const token = await getAccessToken();
  const response = await fetch(`${workflowConfig.apiUrl}/submissions/${encodeURIComponent(id)}/photo`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw await responseError(response);
  return response.blob();
}

export async function getReviewerPhoto(id, getAccessToken) {
  const token = await getAccessToken();
  const response = await fetch(`${workflowConfig.apiUrl}/reviews/${encodeURIComponent(id)}/photo`, {
    headers: { Authorization: `Bearer ${token}` }, cache: 'no-store',
  });
  if (!response.ok) throw await responseError(response);
  return response.blob();
}

export function saveAdminDirectUpdaterScope(email, companyKeys, expectedVersion, reason, getAccessToken) {
  if (reviewerPreviewSandboxEnabled) return sandboxWriteBlocked();
  return request('/management/direct-updater-scopes', getAccessToken, {
    method: 'PUT',
    body: JSON.stringify({ email, companyKeys, expectedVersion, reason }),
  });
}

export const lookupDirectEmployee = (email, getAccessToken) => request(`/direct-updates/employee?email=${encodeURIComponent(email)}`, getAccessToken);
export const searchDirectEmployees = (query, getAccessToken) => request(`/direct-updates/employee?query=${encodeURIComponent(query)}`, getAccessToken);
export const listDirectUpdateHistory = (getAccessToken) => request('/direct-updates/history', getAccessToken);
